// Utility functions for parsing and formatting numbers in Argentine format
// (dot for thousands, comma for decimals)

/**
 * Parses a numeric value that may be in Argentine or standard format
 * Handles ambiguous cases consistently with backend logic
 *
 * Examples:
 *   "1.234,56" -> 1234.56 (full Argentine format)
 *   "123,45" -> 123.45 (only comma, is decimal)
 *   "123.456" -> 123456 (only dot with >2 digits after, are thousands)
 *   "1.5" -> 1.5 (only dot with <=2 digits after, is decimal)
 *
 * ⚠️ AMBIGUITY WARNING: When there's only a dot with 2 or fewer digits after, it's assumed to be decimal.
 *    Example: "100.5" is interpreted as 100.5 (decimal), NOT 1005 (thousands).
 *    To avoid ambiguity, always use comma for decimals: "100,5" or "1.005" for thousands.
 *
 * @param {string|number} value - The value to parse
 * @returns {number} The parsed number
 */
export function parseNumber(value) {
  if (!value || value === "") return 0;

  const valueStr = value.toString().trim();

  // If has both . and ,, assume Argentine format (dot = thousands, comma = decimal)
  if (valueStr.includes('.') && valueStr.includes(',')) {
    return parseFloat(valueStr.replace(/\./g, '').replace(',', '.'));
  }

  // If only has comma, assume it's decimal
  if (valueStr.includes(',') && !valueStr.includes('.')) {
    return parseFloat(valueStr.replace(',', '.'));
  }

  // If only has dot, need to determine if thousands or decimal
  if (valueStr.includes('.')) {
    const parts = valueStr.split('.');
    // If the part after the dot has more than 2 digits, they are thousands
    if (parts.length === 2 && parts[1].length > 2) {
      return parseFloat(valueStr.replace(/\./g, ''));
    }
  }

  return parseFloat(valueStr) || 0;
}

/**
 * Formats a number in Argentine format (dot for thousands, comma for decimals)
 *
 * @param {number|string} number - The number to format
 * @param {number} precision - Number of decimal places (default: 2)
 * @returns {string} The formatted number in Argentine format
 */
export function formatNumberArgentina(number, precision = 2) {
  if (isNaN(number) || number === null || number === undefined) {
    return "0" + (precision > 0 ? "," + "0".repeat(precision) : "");
  }

  const num = parseFloat(number);
  const fixed = num.toFixed(precision);
  const parts = fixed.split(".");
  const integerPart = parts[0];
  const decimalPart = parts[1] || "";

  // Add dots as thousand separators
  const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ".");

  // Combine with comma as decimal separator
  return precision > 0 ? `${formattedInteger},${decimalPart}` : formattedInteger;
}
